/**
 * localStorage utility hook for EMDR Sync app
 * Provides type-safe storage access for app-specific data
 */

export interface AppStorage {
  role: 'therapist' | 'patient' | null;
  hasAcceptedAgreements: boolean;
  userId: string | null;
}

const STORAGE_PREFIX = 'emdr-role-';

/**
 * Type-safe localStorage utility
 */
export function useLocalStorage() {
  /**
   * Get an item from localStorage
   */
  const getItem = <K extends keyof AppStorage>(key: K): AppStorage[K] | null => {
    try {
      const item = localStorage.getItem(`${STORAGE_PREFIX}${key}`);
      if (item === null) return null;
      return JSON.parse(item) as AppStorage[K];
    } catch (error) {
      console.error(`Error reading from localStorage (${key}):`, error);
      return null;
    }
  };

  /**
   * Set an item in localStorage
   */
  const setItem = <K extends keyof AppStorage>(key: K, value: AppStorage[K]): void => {
    try {
      localStorage.setItem(`${STORAGE_PREFIX}${key}`, JSON.stringify(value));
    } catch (error) {
      console.error(`Error writing to localStorage (${key}):`, error);
      // Handle quota exceeded error gracefully
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        console.warn('localStorage quota exceeded. Clearing old data...');
        // Attempt to clear old data and retry
        clearAll();
        try {
          localStorage.setItem(`${STORAGE_PREFIX}${key}`, JSON.stringify(value));
        } catch (retryError) {
          console.error('Failed to write to localStorage after clearing:', retryError);
        }
      }
    }
  };

  /**
   * Remove a specific item from localStorage
   */
  const removeItem = <K extends keyof AppStorage>(key: K): void => {
    try {
      localStorage.removeItem(`${STORAGE_PREFIX}${key}`);
    } catch (error) {
      console.error(`Error removing from localStorage (${key}):`, error);
    }
  };

  /**
   * Clear all app-specific items from localStorage
   */
  const clearAll = (): void => {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith(STORAGE_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  };

  return {
    getItem,
    setItem,
    removeItem,
    clearAll,
  };
}
